<script setup lang="ts">
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { AlertCircle, CheckCircle2 } from 'lucide-vue-next'
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'
import { Button } from '~/components/ui/button'
import { ContentBlock } from '~/components/ui/content-block'
import { FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form'
import { Label } from '~/components/ui/label'
import type { IRatingTarget } from '~/components/entity/rating-target'
import FeatureReviewsAddFormPicker from './FeatureReviewsAddFormPicker.vue'
import FeatureReviewsAddFormStep from './FeatureReviewsAddFormStep.vue'
import { EntityRatingCriteriaSet } from '~/components/entity/rating-criteria-set'
import { Textarea } from '~/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select'
import { useToast } from '~/components/ui/toast'
import { extractRatingErrorMessage, useRating } from '~/composables/useRating'
import {
  ORG_TYPE_CONFIG,
  PRODUCT_TYPE_CONFIG,
  isOrganizationType,
  isProductType,
  type TOrganizationType,
  type TProductType,
  type TReviewTarget
} from './types'

const MAX_COMMENT_LENGTH = 500

const { t } = useI18n()
const { trackEvent } = useAnalytics()
const route = useRoute()
const router = useRouter()
const { toast } = useToast()

const { isLoggedIn } = useUser()
const { openLoginModal } = useAuthModalStore()

// Unlike WidgetRating, this page always requires auth, ignoring
// `ratings_only_for_authorized`.
const { getRatingCriteriaSetsReactive, submitRating } = useRating()

// Pre-compute all validation messages to avoid calling useValidationMessage in computed
const validationMessages = {
  requiredTarget: useValidationMessage('requiredField'),
  requiredOrganizationType: useValidationMessage('requiredField'),
  requiredOrganization: useValidationMessage('requiredField'),
  requiredProduct: useValidationMessage('requiredField')
}

// --- Cascade state ---------------------------------------------------------

// What the review is about: an organization type or a product type.
const target = ref<TReviewTarget | ''>('')
// Only asked when the chosen product exists at more than one organization type.
const orgType = ref<TOrganizationType | ''>('')
const orgId = ref<number | null>(null)
const productId = ref<number | null>(null)

const criteriaValues = ref<Record<number, number>>({})
const comment = ref('')

// Set by the pickers' @update:selected-item, since each picker now owns its
// own paged MeiliSearch fetch instead of handing the parent a full list to
// look the id up in.
const selectedOrganization = ref<IRatingTarget | null>(null)
const selectedProduct = ref<IRatingTarget | null>(null)

const isSubmitting = ref(false)
const submitSuccess = ref(false)
const submitError = ref('')

// --- Query prefill ---------------------------------------------------------

const parseTarget = (value: unknown): TReviewTarget | '' => {
  if (typeof value !== 'string') return ''
  return isProductType(value) || isOrganizationType(value) ? value : ''
}

const parseOrgType = (value: unknown): TOrganizationType | '' => {
  return typeof value === 'string' && isOrganizationType(value) ? value : ''
}

const parseId = (value: unknown): number | null => {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

// One-way prefill, no state -> URL sync. Must run BEFORE the watchers below,
// or the target watcher clears everything downstream.
target.value = parseTarget(route.query.target)
orgType.value = parseOrgType(route.query.org_type)
orgId.value = target.value ? parseId(route.query.org) : null
productId.value = target.value && isProductType(target.value) ? parseId(route.query.product) : null

// --- Derived ---------------------------------------------------------------

const needsProduct = computed(() => !!target.value && isProductType(target.value))

const productConfig = computed(() =>
  target.value && isProductType(target.value) ? PRODUCT_TYPE_CONFIG[target.value] : null
)

const allowedOrgTypes = computed<TOrganizationType[]>(() => {
  if (productConfig.value) return productConfig.value.orgTypes
  return target.value && isOrganizationType(target.value) ? [target.value] : []
})

// A product offered by a single organization type needs no type question.
const needsOrgTypeStep = computed(() => allowedOrgTypes.value.length > 1)

// Collapses to the only allowed value when the type step is skipped, so both
// branches share one code path.
const effectiveOrgType = computed<TOrganizationType | ''>(() =>
  needsOrgTypeStep.value ? orgType.value : (allowedOrgTypes.value[0] ?? '')
)

const rateableType = computed<string | null>(() => target.value || null)
const rateableId = computed(() => (needsProduct.value ? productId.value : orgId.value))
const isTargetResolved = computed(() => !!rateableType.value && !!rateableId.value)

const targetOptionGroups = computed(() => [
  {
    labelKey: 'feature.reviews_add_form.groups.organization',
    options: (Object.keys(ORG_TYPE_CONFIG) as TOrganizationType[]).map((type) => ({
      value: type,
      label: t(ORG_TYPE_CONFIG[type].labelKey)
    }))
  },
  {
    labelKey: 'feature.reviews_add_form.groups.product',
    options: (Object.keys(PRODUCT_TYPE_CONFIG) as TProductType[]).map((type) => ({
      value: type,
      label: t(PRODUCT_TYPE_CONFIG[type].labelKey)
    }))
  }
])

const organizationTypeOptions = computed(() =>
  allowedOrgTypes.value.map((type) => ({
    value: type,
    label: t(ORG_TYPE_CONFIG[type].labelKey)
  }))
)

// When the type step is skipped the organization select names the type
// outright ("Bank") instead of the generic "Organization".
const organizationLabel = computed(() =>
  !needsOrgTypeStep.value && effectiveOrgType.value
    ? t(ORG_TYPE_CONFIG[effectiveOrgType.value].labelKey)
    : t('feature.reviews_add_form.labels.organization')
)

const isOrganizationListPaginated = computed(
  () => (effectiveOrgType.value ? (ORG_TYPE_CONFIG[effectiveOrgType.value].paginated ?? true) : true)
)

// --- MeiliSearch pickers -----------------------------------------------------

const organizationMeiliIndex = computed(() =>
  effectiveOrgType.value ? ORG_TYPE_CONFIG[effectiveOrgType.value].meiliIndex : ''
)

const productMeiliIndex = computed(() => productConfig.value?.meiliIndex ?? '')

// organization.id/type filter scopes the product picker to what was chosen
// in the previous step.
const productFilter = computed(() =>
  orgId.value && effectiveOrgType.value
    ? `organization.id = ${orgId.value} AND organization.type = "${effectiveOrgType.value}"`
    : undefined
)

const hasNoProducts = ref(false)

// Cleared whenever the upstream cascade choice changes, so a stale card from
// a previous organization/product never lingers in FeatureReviewsAddFormStep.
watch(effectiveOrgType, () => {
  selectedOrganization.value = null
})

watch([productConfig, orgId], () => {
  selectedProduct.value = null
  // Avoids a stale "no products" alert flashing while the new fetch is pending.
  hasNoProducts.value = false
})

// --- Criteria --------------------------------------------------------------

const { data: criteriaSetsData, status: criteriaStatus } =
  getRatingCriteriaSetsReactive(rateableType)

// Each rateable_type maps to exactly one active criteria set, and one review
// produces one rating — so take the first set rather than looping over them.
const criteriaSet = computed(() => criteriaSetsData.value?.data?.[0] ?? null)

// Gated on the whole cascade, not just `rateableType` — a product target
// (e.g. credit) still needs org type + organization + product picked first.
const isCriteriaLoading = computed(
  () =>
    isTargetResolved.value &&
    (criteriaStatus.value === 'pending' || criteriaStatus.value === 'idle')
)

// Checked against the target alone so the user is told before working through
// the rest of the cascade (exchange offices have no criteria set today).
const hasNoCriteria = computed(
  () => !!rateableType.value && criteriaStatus.value === 'success' && !criteriaSet.value
)

const totalCriteria = computed(() => criteriaSet.value?.criteria?.length ?? 0)

const ratedCriteriaCount = computed(
  () =>
    criteriaSet.value?.criteria?.filter((criteria) => criteriaValues.value[criteria.id] > 0)
      .length ?? 0
)

const criteriaComplete = computed(
  () => totalCriteria.value > 0 && ratedCriteriaCount.value === totalCriteria.value
)

const remainingCriteriaMessage = computed(() => {
  const remaining = totalCriteria.value - ratedCriteriaCount.value
  if (remaining <= 0) return ''

  const word =
    remaining === 1
      ? t('widget.rating.form.criteria_word_one')
      : remaining < 5
        ? t('widget.rating.form.criteria_word_few')
        : t('widget.rating.form.criteria_word_many')

  return t('widget.rating.form.remaining_criteria', { count: remaining, word })
})

const commentLength = computed(() => comment.value.length)
const isCommentTooLong = computed(() => commentLength.value > MAX_COMMENT_LENGTH)

// --- Cascade watchers (flat, no chaining) ----------------------------------

watch(target, () => {
  orgType.value = ''
  orgId.value = null
  productId.value = null
})

watch(orgType, () => {
  orgId.value = null
  productId.value = null
})

watch(orgId, () => {
  productId.value = null
})

// Watching criteriaSet alone is not enough — it is keyed on rateable_type, so
// switching between two credits would carry the previous stars over.
watch([rateableType, rateableId], () => {
  criteriaValues.value = {}
  comment.value = ''
  submitError.value = ''
})

// --- Form ------------------------------------------------------------------

const formSchema = computed(() =>
  toTypedSchema(
    z.object({
      target: z
        .string({ required_error: validationMessages.requiredTarget })
        .min(1, { message: validationMessages.requiredTarget }),
      organization_type: needsOrgTypeStep.value
        ? z
            .string({ required_error: validationMessages.requiredOrganizationType })
            .min(1, { message: validationMessages.requiredOrganizationType })
        : z.string().optional(),
      organization_id: z
        .number({ required_error: validationMessages.requiredOrganization })
        .min(1, { message: validationMessages.requiredOrganization }),
      product_id: needsProduct.value
        ? z
            .number({ required_error: validationMessages.requiredProduct })
            .min(1, { message: validationMessages.requiredProduct })
        : z.number().optional()
    })
  )
)

// The comment is intentionally outside the schema: it lives in a plain ref
// alongside the criteria values, which are also not schema-backed.
const form = useForm({
  validationSchema: formSchema,
  initialValues: {
    target: target.value || undefined,
    organization_type: orgType.value || undefined,
    organization_id: orgId.value ?? undefined,
    product_id: productId.value ?? undefined
  }
})

// The cascade refs mirror vee-validate's values so the derived computeds and
// fetches read from one place.
const syncTarget = (value: unknown) => {
  target.value = parseTarget(value)
  form.resetField('organization_type')
  form.resetField('organization_id')
  form.resetField('product_id')
}

const syncOrgType = (value: unknown) => {
  orgType.value = parseOrgType(value)
  form.resetField('organization_id')
  form.resetField('product_id')
}

const syncOrgId = (value: unknown) => {
  orgId.value = parseId(value)
  // Clearing uses resetField so the required rule does not fire while choosing.
  if (orgId.value === null) form.resetField('organization_id')
  else form.setFieldValue('organization_id', orgId.value)
  form.resetField('product_id')
}

const syncProductId = (value: unknown) => {
  productId.value = parseId(value)
  if (productId.value === null) form.resetField('product_id')
  else form.setFieldValue('product_id', productId.value)
}

const canSubmit = computed(
  () =>
    isTargetResolved.value &&
    !!criteriaSet.value &&
    criteriaComplete.value &&
    !isCommentTooLong.value &&
    !isSubmitting.value
)

const isRateLimitError = (error: unknown): boolean => {
  return !!error && typeof error === 'object' && (error as Record<string, unknown>).status === 429
}

const onSubmit = form.handleSubmit(async () => {
  if (!canSubmit.value || !criteriaSet.value || !rateableType.value || !rateableId.value) return

  submitError.value = ''
  isSubmitting.value = true

  try {
    const values = Object.entries(criteriaValues.value).map(([criteriaId, value]) => ({
      criteria_id: Number(criteriaId),
      value
    }))

    const { error } = await submitRating({
      rateable_id: rateableId.value,
      rateable_type: rateableType.value,
      criteria_set_id: criteriaSet.value.id,
      values,
      ...(comment.value.trim() && { comment: comment.value.trim() })
    })

    if (error) {
      if (isRateLimitError(error)) {
        toast({
          title: t('error.title'),
          description: t('feature.reviews_add_form.errors.too_many_attempts'),
          variant: 'destructive'
        })
      } else {
        submitError.value = extractRatingErrorMessage(
          error,
          t('feature.reviews_add_form.errors.submit_failed')
        )
      }
      return
    }

    submitSuccess.value = true
    trackEvent('submit_review', { rateable_type: rateableType.value, product_id: rateableId.value })
  } catch (error: unknown) {
    submitError.value = extractRatingErrorMessage(
      error,
      t('feature.reviews_add_form.errors.submit_failed')
    )
  } finally {
    isSubmitting.value = false
  }
})

// Also backs the "leave another review" action.
const resetForm = () => {
  target.value = ''
  orgType.value = ''
  orgId.value = null
  productId.value = null
  criteriaValues.value = {}
  comment.value = ''
  submitError.value = ''
  submitSuccess.value = false
  form.resetForm()

  // Drop the prefill params too, or reloading the page restores what was just
  // cleared. Other query keys are left untouched.
  const { target: _t, org_type: _ot, org: _o, product: _p, ...rest } = route.query
  router.replace({ path: route.path, query: rest })
}

const hasAnyInput = computed(
  () => !!target.value || !!comment.value || Object.keys(criteriaValues.value).length > 0
)
</script>

<template>
  <div>
    <template v-if="isLoggedIn">
      <div v-if="submitSuccess" class="space-y-4">
        <Alert variant="green">
          <CheckCircle2 class="h-4 w-4" />
          <AlertTitle>{{ $t('feature.reviews_add_form.success.title') }}</AlertTitle>
          <AlertDescription>
            {{ $t('feature.reviews_add_form.success.description') }}
          </AlertDescription>
        </Alert>

        <Button type="button" variant="outline-blue" @click="resetForm">
          {{ $t('feature.reviews_add_form.success.add_another') }}
        </Button>
      </div>

      <form
        v-else
        v-webmcp-tool="{
          name: 'write_review',
          description:
            'Write a review about a bank or another financial organization of Kyrgyzstan with a rating. The review is published on the site after moderation.'
        }"
        class="flex flex-col gap-5"
        @submit="onSubmit"
      >
        <FormField v-slot="{ componentField, errors }" name="target">
          <FormItem>
            <FormLabel>{{ $t('feature.reviews_add_form.labels.target') }}*</FormLabel>
            <Select v-bind="componentField" @update:model-value="syncTarget">
              <SelectTrigger
                variant="searchForm"
                :aria-label="$t('feature.reviews_add_form.placeholders.target')"
                :aria-invalid="errors.length > 0"
              >
                <SelectValue :placeholder="$t('feature.reviews_add_form.placeholders.target')" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup v-for="group in targetOptionGroups" :key="group.labelKey">
                  <SelectLabel>{{ $t(group.labelKey) }}</SelectLabel>
                  <SelectItem
                    v-for="option in group.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            <FormMessage v-if="errors.length" :message="errors[0]" />
          </FormItem>
        </FormField>

        <Alert v-if="hasNoCriteria" variant="default">
          <AlertCircle class="h-4 w-4" />
          <AlertDescription>
            {{ $t('feature.reviews_add_form.empty.no_criteria') }}
          </AlertDescription>
        </Alert>

        <template v-else>
          <FormField
            v-if="needsOrgTypeStep"
            v-slot="{ componentField, errors }"
            name="organization_type"
          >
            <FormItem>
              <FormLabel>
                {{ $t('feature.reviews_add_form.labels.organization_type') }}*
              </FormLabel>
              <Select v-bind="componentField" @update:model-value="syncOrgType">
                <SelectTrigger
                  variant="searchForm"
                  :aria-label="$t('feature.reviews_add_form.placeholders.organization_type')"
                  :aria-invalid="errors.length > 0"
                >
                  <SelectValue
                    :placeholder="$t('feature.reviews_add_form.placeholders.organization_type')"
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="option in organizationTypeOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>

          <ClientOnly v-if="effectiveOrgType">
            <FormField v-slot="{ errors }" name="organization_id">
              <FormItem>
                <FeatureReviewsAddFormStep
                  :label="organizationLabel"
                  :selected-item="selectedOrganization"
                  @change="syncOrgId(null)"
                >
                  <FeatureReviewsAddFormPicker
                    :meili-index="organizationMeiliIndex"
                    :attributes-to-search-on="['title.ky', 'title.ru']"
                    :model-value="orgId"
                    :paginated="isOrganizationListPaginated"
                    :empty-text="$t('feature.reviews_add_form.empty.no_organizations')"
                    :search-placeholder="$t('feature.reviews_add_form.picker.search_organization')"
                    @update:model-value="syncOrgId"
                    @update:selected-item="selectedOrganization = $event"
                  />
                </FeatureReviewsAddFormStep>
                <FormMessage v-if="errors.length" :message="errors[0]" />
              </FormItem>
            </FormField>
          </ClientOnly>

          <ClientOnly v-if="needsProduct && orgId">
            <Alert v-if="hasNoProducts" variant="default">
              <AlertCircle class="h-4 w-4" />
              <AlertDescription>
                {{ $t('feature.reviews_add_form.empty.no_products') }}
              </AlertDescription>
            </Alert>

            <FormField v-else v-slot="{ errors }" name="product_id">
              <FormItem>
                <FeatureReviewsAddFormStep
                  :label="$t('feature.reviews_add_form.labels.product')"
                  :selected-item="selectedProduct"
                  @change="syncProductId(null)"
                >
                  <FeatureReviewsAddFormPicker
                    :meili-index="productMeiliIndex"
                    :filter="productFilter"
                    :attributes-to-search-on="['title.ky', 'title.ru']"
                    :model-value="productId"
                    :empty-text="$t('feature.reviews_add_form.empty.no_products')"
                    :search-placeholder="$t('feature.reviews_add_form.picker.search_product')"
                    @update:model-value="syncProductId"
                    @update:selected-item="selectedProduct = $event"
                    @update:empty="hasNoProducts = $event"
                  />
                </FeatureReviewsAddFormStep>
                <FormMessage v-if="errors.length" :message="errors[0]" />
              </FormItem>
            </FormField>
          </ClientOnly>

          <ContentBlock v-if="isCriteriaLoading" variant="white">
            <div class="mb-4 h-7 w-48 animate-pulse rounded bg-gray-200" />
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div v-for="i in 4" :key="i" class="animate-pulse space-y-2">
                <div class="h-4 w-20 rounded bg-gray-200" />
                <div class="h-4 w-3/4 rounded bg-gray-200" />
                <div class="h-3 w-full rounded bg-gray-200" />
              </div>
            </div>
          </ContentBlock>

          <template v-else-if="isTargetResolved && criteriaSet">
            <ContentBlock
              variant="white"
              :heading-text="criteriaSet.name"
              heading-tag="h2"
              heading-class="text-lg font-semibold text-gray-900"
            >
              <EntityRatingCriteriaSet
                :key="criteriaSet.id"
                v-model="criteriaValues"
                :criteria="criteriaSet.criteria ?? []"
              />

              <div class="space-y-1.5">
                <Label for="review-comment">
                  {{ $t('feature.reviews_add_form.labels.comment') }}
                </Label>
                <Textarea
                  id="review-comment"
                  v-model="comment"
                  :maxlength="MAX_COMMENT_LENGTH"
                  :placeholder="$t('widget.rating.form.comment_placeholder')"
                  rows="4"
                  class="resize-none"
                  :class="{ 'border-red-300': isCommentTooLong }"
                />
                <div class="flex justify-between text-sm">
                  <span v-if="isCommentTooLong" class="text-red-600">
                    {{ $t('widget.rating.form.comment_too_long') }}
                  </span>
                  <span v-else class="text-gray-500">
                    {{ $t('widget.rating.form.comment_max') }}
                  </span>
                  <span :class="isCommentTooLong ? 'text-red-600' : 'text-gray-500'">
                    {{ commentLength }}/{{ MAX_COMMENT_LENGTH }}
                  </span>
                </div>
              </div>

              <Alert v-if="submitError" variant="red">
                <AlertCircle class="h-4 w-4" />
                <AlertDescription>{{ submitError }}</AlertDescription>
              </Alert>

              <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <p v-if="remainingCriteriaMessage" class="text-sm text-gray-500">
                  {{ remainingCriteriaMessage }}
                </p>
                <span v-else />

                <div class="flex items-center justify-end gap-3">
                  <Button
                    v-if="hasAnyInput"
                    type="button"
                    variant="outline-gray"
                    :disabled="isSubmitting"
                    @click="resetForm"
                  >
                    {{ $t('feature.reviews_add_form.reset') }}
                  </Button>

                  <Button type="submit" :disabled="!canSubmit">
                    {{
                      isSubmitting
                        ? $t('widget.rating.buttons.saving')
                        : $t('feature.reviews_add_form.submit')
                    }}
                  </Button>
                </div>
              </div>
            </ContentBlock>
          </template>
        </template>
      </form>
    </template>

    <template v-else>
      <div class="border-t border-gray-200 py-8 text-center">
        <p class="mb-4 text-gray-600">
          {{ $t('feature.reviews_add_form.login_prompt') }}
        </p>
        <Button variant="blue" class="px-6 py-2" @click="openLoginModal">
          {{ $t('common.sign_in') }}
        </Button>
      </div>
    </template>
  </div>
</template>
